/**
 * The visitor's tracking-cookie consent, as the backend needs to see it
 * (prawmi-ibut).
 *
 * The choice lives in the `prawmi-cookie-consent` cookie, which
 * CookieConsentBanner / CookiePreferencesModal write host-only on prawmi.pl:
 * `accepted` or `refused`, and no cookie at all until the visitor chooses.
 * The API is on api.prawmi.pl and never receives that cookie, so every call
 * that relies on the backend's visit cookie (`prawmi_session_id`) states the
 * consent itself - the backend then sets a 1-year identifier only with
 * `granted`, and a session-only one otherwise (backend/src/mnb_activity/consent.py).
 *
 * Only an explicit accept counts: `refused`, no choice yet, an unreadable
 * cookie and server-side rendering are all `denied`.
 *
 * Usable from both halves of the app: `app/**` imports it as
 * `@/utils/trackingConsent`, `src/**` by relative path.
 */

export const CONSENT_HEADER = 'X-Prawmi-Consent';

/** The cookie the consent banner writes, and its "accept" value. */
const CONSENT_COOKIE = 'prawmi-cookie-consent';
const ACCEPTED = 'accepted';

export type TrackingConsent = 'granted' | 'denied';

function readConsentCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/** True only when the visitor explicitly accepted tracking cookies. */
export function hasTrackingConsent(): boolean {
  return readConsentCookie() === ACCEPTED;
}

/**
 * The value beacons put in their JSON body as `consent`:
 * `navigator.sendBeacon` cannot carry the header.
 */
export function trackingConsent(): TrackingConsent {
  return hasTrackingConsent() ? 'granted' : 'denied';
}

export function consentHeaders(): Record<string, string> {
  return { [CONSENT_HEADER]: trackingConsent() };
}
